import { fetchWithAuth, getIsSessionExpired } from '../AuthService';
import { DATA_SERVICE_BASE_URL } from '../ApiService';

/**
 * UsageEventsApi
 * Endpoints under /api/stats/events and related subpaths.
 */
export const UsageEventsApi = {
  /** Record a single usage event */
  recordEvent: async (event) => {
    if (getIsSessionExpired()) return { ok: false, status: 0 };
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/stats/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(event)
    });
  },
  /** Record events batch */
  recordEventsBatch: async (events, { keepalive = false, correlationId = null } = {}) => {
    // Skip during session expiry to prevent error cascade (401 → error → report → fetch → 401...)
    if (getIsSessionExpired()) return { ok: false, status: 0 };
    // keepalive:true helps attempt delivery during visibility/pagehide, limited payload size (<64kb) & no streaming
    const headers = { 'Content-Type': 'application/json' };
    if (correlationId) {
      headers['X-Correlation-ID'] = correlationId;
    }
    const resp = await fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/stats/events/batch`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ events }),
      keepalive
    });
    const status = resp ? resp.status : 'no-response';
    let bodyText = '';
    try { bodyText = resp && resp.text ? await resp.clone().text() : ''; } catch { /* ignore */ }
    const snippet = bodyText.slice(0, 180);
    if (!resp || !resp.ok) {
      // eslint-disable-next-line no-console
      console.warn('[UsageEventsApi] batch failure', { status, snippet, length: bodyText.length });
      const err = new Error(`UsageEvents batch failed status=${status}`);
      err.status = status;
      err.body = bodyText;
      throw err;
    } else {
      // eslint-disable-next-line no-console
      console.log('[UsageEventsApi] batch success', { status, snippet, length: bodyText.length });
    }
    return resp; // caller doesn't parse body currently
  },
  /** Get per-user summary */
  getUserSummary: async ({ userId, from, to }) => {
    const params = new URLSearchParams();
    if (from) params.set('from', typeof from === 'string' ? from : from.toISOString());
    if (to) params.set('to', typeof to === 'string' ? to : to.toISOString());
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/stats/events/user/${userId}/summary?${params}`);
  },
  /** Get recent events of a given type for user */
  getUserRecentByType: async ({ userId, eventType }) => {
    const params = new URLSearchParams();
    if (eventType) params.set('eventType', eventType);
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/stats/events/user/${userId}/recent?${params}`);
  },
  /** Admin app-level summary */
  getAppSummary: async ({ from, to }) => {
    const params = new URLSearchParams();
    if (from) params.set('from', typeof from === 'string' ? from : from.toISOString());
    if (to) params.set('to', typeof to === 'string' ? to : to.toISOString());
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/stats/events/admin/app-summary?${params}`);
  },

  // --- NEW: Generic Usage Event Stats (User scope) ---
  userSearch: async ({ userId, eventType, sessionId, correlationId, from, to, page=0, size=50 }) => {
    const params = new URLSearchParams();
    if (eventType) params.set('eventType', eventType);
    if (sessionId) params.set('sessionId', sessionId);
    if (correlationId) params.set('correlationId', correlationId);
    if (from) params.set('from', typeof from === 'string' ? from : from.toISOString());
    if (to) params.set('to', typeof to === 'string' ? to : to.toISOString());
    params.set('page', page);
    params.set('size', size);
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/stats/events/user/${userId}/search?${params}`);
  },
  userTopTypes: async ({ userId, from, to, limit=10 }) => {
    const params = new URLSearchParams();
    if (from) params.set('from', typeof from === 'string' ? from : from.toISOString());
    if (to) params.set('to', typeof to === 'string' ? to : to.toISOString());
    if (limit) params.set('limit', limit);
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/stats/events/user/${userId}/top-types?${params}`);
  },
  userTimeSeries: async ({ userId, eventType, from, to, bucket }) => {
    const params = new URLSearchParams();
    if (eventType) params.set('eventType', eventType);
    if (from) params.set('from', typeof from === 'string' ? from : from.toISOString());
    if (to) params.set('to', typeof to === 'string' ? to : to.toISOString());
    if (bucket) params.set('bucket', bucket);
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/stats/events/user/${userId}/time-series?${params}`);
  },
  userActiveSessions: async ({ userId, from, to, limit=20 }) => {
    const params = new URLSearchParams();
    if (from) params.set('from', typeof from === 'string' ? from : from.toISOString());
    if (to) params.set('to', typeof to === 'string' ? to : to.toISOString());
    if (limit) params.set('limit', limit);
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/stats/events/user/${userId}/active-sessions?${params}`);
  },
  userUsageStats: async ({ userId, from, to }) => {
    const params = new URLSearchParams();
    if (from) params.set('from', typeof from === 'string' ? from : from.toISOString());
    if (to) params.set('to', typeof to === 'string' ? to : to.toISOString());
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/stats/events/user/${userId}/usage-stats?${params}`);
  },

  // --- NEW: Generic Usage Event Stats (Admin scope) ---
  adminSearch: async ({ userId, eventType, sessionId, correlationId, from, to, page=0, size=100 }) => {
    const params = new URLSearchParams();
    if (userId) params.set('userId', userId);
    if (eventType) params.set('eventType', eventType);
    if (sessionId) params.set('sessionId', sessionId);
    if (correlationId) params.set('correlationId', correlationId);
    if (from) params.set('from', typeof from === 'string' ? from : from.toISOString());
    if (to) params.set('to', typeof to === 'string' ? to : to.toISOString());
    params.set('page', page);
    params.set('size', size);
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/stats/events/admin/search?${params}`);
  },
  adminTopTypes: async ({ userId, from, to, limit=10 }) => {
    const params = new URLSearchParams();
    if (userId) params.set('userId', userId);
    if (from) params.set('from', typeof from === 'string' ? from : from.toISOString());
    if (to) params.set('to', typeof to === 'string' ? to : to.toISOString());
    if (limit) params.set('limit', limit);
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/stats/events/admin/top-types?${params}`);
  },
  adminTimeSeries: async ({ userId, eventType, from, to, bucket }) => {
    const params = new URLSearchParams();
    if (userId) params.set('userId', userId);
    if (eventType) params.set('eventType', eventType);
    if (from) params.set('from', typeof from === 'string' ? from : from.toISOString());
    if (to) params.set('to', typeof to === 'string' ? to : to.toISOString());
    if (bucket) params.set('bucket', bucket);
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/stats/events/admin/time-series?${params}`);
  },
  adminActiveSessions: async ({ userId, from, to, limit=20, anonymousOnly }) => {
    const params = new URLSearchParams();
    if (userId) params.set('userId', userId);
    if (from) params.set('from', typeof from === 'string' ? from : from.toISOString());
    if (to) params.set('to', typeof to === 'string' ? to : to.toISOString());
    if (limit) params.set('limit', limit);
    if (anonymousOnly !== undefined) params.set('anonymousOnly', anonymousOnly);
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/stats/events/admin/active-sessions?${params}`);
  },
  adminUsageStats: async ({ userId, from, to }) => {
    const params = new URLSearchParams();
    if (userId) params.set('userId', userId);
    if (from) params.set('from', typeof from === 'string' ? from : from.toISOString());
    if (to) params.set('to', typeof to === 'string' ? to : to.toISOString());
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/stats/events/admin/usage-stats?${params}`);
  }
  ,
  /** Admin: recent users activity (latest events across distinct users, paged) */
  adminRecentUsers: async ({ from, to, page = 0, size = 50 } = {}) => {
    const params = new URLSearchParams();
    params.set('page', page);
    params.set('size', size);
    if (from) params.set('from', typeof from === 'string' ? from : from.toISOString());
    if (to) params.set('to', typeof to === 'string' ? to : to.toISOString());
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/stats/events/admin/recent-users?${params}`);
  }
};

export default UsageEventsApi;
